import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, AlertTriangle, RefreshCw, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PlatformCard from './components/PlatformCard';
import {
    getIntegrations,
    getOAuthUrl,
    deleteIntegration,
    refreshIntegration
} from '../../api/socialMedia';
import { getSocialMediaLimit, normalizeTier } from '../../utils/subscriptionHelpers';

const SUPPORTED_PLATFORMS = ['youtube', 'facebook', 'instagram', 'linkedin'];

const PlatformConnections = ({ subscription }) => {
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [connectingPlatform, setConnectingPlatform] = useState(null);
    const [disconnectingId, setDisconnectingId] = useState(null);
    const [confirmDisconnect, setConfirmDisconnect] = useState(null);

    // Get tier and limits
    const tier = normalizeTier(subscription?.tier || subscription?.plan?.tier || subscription?.plan?.name);
    const integrationLimit = getSocialMediaLimit(tier);
    const connectedCount = integrations.length;
    const canConnectMore = connectedCount < integrationLimit;

    // Load integrations
    const loadIntegrations = useCallback(async () => {
        try {
            setError(null);
            const response = await getIntegrations();
            setIntegrations(response.data || []);
        } catch (err) {
            console.error('Failed to load integrations:', err);
            setError(err.message || 'Failed to load connected accounts');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadIntegrations();
    }, [loadIntegrations]);

    // Handle connect
    const handleConnect = async (platform) => {
        if (!canConnectMore) {
            setError(`You've reached your limit of ${integrationLimit} connected accounts. Upgrade to connect more.`);
            return;
        }

        try {
            setConnectingPlatform(platform);
            setError(null);

            const response = await getOAuthUrl(platform);
            console.log('[Social Media] OAuth URL response:', response);

            // Handle various response formats from the API
            const data = response.data || response;
            const url = data.authUrl || data.url || data.oauth_url || data.authorization_url;
            const state = data.state;

            if (!url) {
                console.error('[Social Media] No OAuth URL in response:', response);
                throw new Error('No OAuth URL received from server');
            }

            // Store state for CSRF verification
            if (state) {
                sessionStorage.setItem('social_oauth_state', state);
            }
            sessionStorage.setItem('social_oauth_platform', platform);

            console.log('[Social Media] Redirecting to:', url);

            // Redirect to OAuth provider
            window.location.href = url;
        } catch (err) {
            console.error('Failed to initiate OAuth:', err);
            setError(err.message || `Failed to connect to ${platform}`);
            setConnectingPlatform(null);
        }
    };

    // Handle disconnect
    const handleDisconnect = async (integrationId) => {
        try {
            setDisconnectingId(integrationId);
            setError(null);

            await deleteIntegration(integrationId);
            setIntegrations(prev => prev.filter(i => i.id !== integrationId));
            setConfirmDisconnect(null);
        } catch (err) {
            console.error('Failed to disconnect:', err);
            setError(err.message || 'Failed to disconnect account');
        } finally {
            setDisconnectingId(null);
        }
    };

    // Handle refresh
    const handleRefresh = async (integrationId) => {
        try {
            setError(null);
            const integration = integrations.find(i => i.id === integrationId);
            if (integration) {
                // Re-initiate OAuth for refresh
                await handleConnect(integration.platform);
            }
        } catch (err) {
            console.error('Failed to refresh:', err);
            setError(err.message || 'Failed to refresh connection');
        }
    };

    // Get integration for a platform
    const getIntegrationForPlatform = (platform) => {
        return integrations.find(i => i.platform === platform);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-8">
            {/* Header Stats */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                            Connected Accounts
                        </h2>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                            Manage your social media platform connections
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="text-2xl font-bold text-slate-900 dark:text-white">
                            {connectedCount} / {integrationLimit}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            accounts connected
                        </p>
                    </div>
                </div>

                {/* Progress bar */}
                <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(connectedCount / integrationLimit) * 100}%` }}
                        className={`h-full rounded-full ${
                            connectedCount >= integrationLimit
                                ? 'bg-yellow-500'
                                : 'bg-yellow-500'
                        }`}
                    />
                </div>
            </div>

            {/* Error Message */}
            <AnimatePresence>
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3"
                    >
                        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                        </div>
                        <button
                            onClick={() => setError(null)}
                            className="text-red-500 hover:text-red-700 dark:hover:text-red-300"
                        >
                            <span className="sr-only">Dismiss</span>
                            ×
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Platform Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {SUPPORTED_PLATFORMS.map((platform) => (
                    <PlatformCard
                        key={platform}
                        platform={platform}
                        integration={getIntegrationForPlatform(platform)}
                        isConnecting={connectingPlatform === platform}
                        onConnect={handleConnect}
                        onDisconnect={(id) => setConfirmDisconnect(id)}
                        onRefresh={handleRefresh}
                    />
                ))}
            </div>

            {/* Disconnect Confirmation Modal */}
            <AnimatePresence>
                {confirmDisconnect && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
                        onClick={() => setConfirmDisconnect(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-md p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-xl"
                        >
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                                Disconnect Account?
                            </h3>
                            <p className="text-slate-600 dark:text-slate-400 mb-6">
                                This will remove the connection and you'll need to reconnect to post to this account again. Scheduled posts for this account will be cancelled.
                            </p>
                            <div className="flex items-center justify-end gap-3">
                                <button
                                    onClick={() => setConfirmDisconnect(null)}
                                    className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleDisconnect(confirmDisconnect)}
                                    disabled={disconnectingId === confirmDisconnect}
                                    className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2"
                                >
                                    {disconnectingId === confirmDisconnect ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Disconnecting...
                                        </>
                                    ) : (
                                        'Disconnect'
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Help Text */}
            <div className="mt-8 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <h4 className="font-medium text-slate-900 dark:text-white mb-2">
                    Tips for connecting accounts
                </h4>
                <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                    <li className="flex items-start gap-2">
                        <span className="text-yellow-500">•</span>
                        Make sure you're logged into the correct account before connecting
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="text-yellow-500">•</span>
                        For Instagram, you'll need a Facebook Business Page linked to your account
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="text-yellow-500">•</span>
                        YouTube requires a channel to be set up before connecting
                    </li>
                </ul>
            </div>
        </div>
    );
};

export default PlatformConnections;
